import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sprout, MapPin, Award, Heart, ArrowRight, Check, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Onboarding({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);

  // If already onboarded, send to dashboard
  React.useEffect(() => {
    if (user?.onboarded) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);

  const [data, setData] = useState({
    location: '',
    latitude: null,
    longitude: null,
    country: 'New Zealand',
    climate_zone: '',
    region: '',
    skill_level: 'beginner',
    garden_type: '',
    garden_size: '',
    growing_preference: 'mixed',
    price_source: 'regional_average',
    community_mode: 'view_only',
    favorites: [],
    ai_choice: 'c'
  });

  const totalSteps = 7;

  const detectLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          
          try {
            const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=10`);
            const result = await response.json();
            const city = result.address?.city || result.address?.town || result.address?.suburb || '';
            const country = result.address?.country || '';
            
            setData(prev => ({
              ...prev,
              latitude: lat,
              longitude: lon,
              location: `${city}, ${country}`,
              country: country || prev.country
            }));
          } catch (e) {
            setData(prev => ({
              ...prev,
              latitude: lat,
              longitude: lon,
              location: `${lat.toFixed(2)}, ${lon.toFixed(2)}`
            }));
          }
          setLocationLoading(false);
        },
        (error) => {
          console.error('Location error:', error);
          setLocationLoading(false);
          alert('Could not detect location. Please enter manually.');
        }
      );
    } else {
      setLocationLoading(false);
      alert('Geolocation not supported by your browser.');
    }
  };

  const handleNext = async () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      await handleComplete();
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    const aiFlags = data.ai_choice === 'a'
      ? { ai_coach_enabled: false, intelligence_enabled: false, ai_coach_forever_disabled: true }
      : { ai_coach_enabled: true, intelligence_enabled: true, ai_coach_forever_disabled: false };
    try {
      await axios.post(`${API}/onboarding/complete`, { ...data, ...aiFlags }, { withCredentials: true });
      navigate('/dashboard');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      setLoading(false);
    }
  };

  const toggleFavorite = (cropName) => {
    setData(prev => ({
      ...prev,
      favorites: prev.favorites.includes(cropName)
        ? prev.favorites.filter(f => f !== cropName)
        : [...prev.favorites, cropName]
    }));
  };

  const popularCrops = ['Tomato', 'Lettuce', 'Carrot', 'Broccoli', 'Cucumber', 'Zucchini', 'Beans', 'Spinach', 'Basil', 'Strawberry', 'Capsicum', 'Garlic'];

  const aiOptions = [
    { value: 'c', label: 'Yes please!', desc: 'Turn on the AI coach and all its smart, tailored advice.' },
    { value: 'b', label: "I'm curious — yes please, but let me switch it on or off", desc: 'Turn the AI coach on now, and toggle it anytime in Settings.' },
    { value: 'a', label: "No thanks — I don't want any AI!", desc: 'Keep Homegrown completely AI-free. Every AI feature stays permanently hidden.' }
  ];

  return (
    <div className="min-h-screen bg-[#E5EBDD] flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-[#7A6B57]">{t('onboarding.stepOf', { step, total: totalSteps })}</span>
            <span className="text-sm font-medium text-[#1A4331]">{Math.round((step / totalSteps) * 100)}%</span>
          </div>
          <div className="h-2 bg-[#D9E1CB] rounded-full overflow-hidden">
            <div className="h-full bg-[#1A4331] transition-all duration-500" style={{ width: `${(step / totalSteps) * 100}%` }}></div>
          </div>
        </div>

        <div className="bg-[#FAF6EC] rounded-3xl p-8 md:p-12 border border-[#1A4331]/10 shadow-sm">
          {step === 1 && (
            <div data-testid="onboarding-step-1">
              <div className="w-16 h-16 bg-[#1A4331] rounded-full flex items-center justify-center mb-6">
                <Sprout className="w-8 h-8 text-[#B4D330]" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A4331] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('onboarding.welcomeTitle')}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
              </h2>
              <p className="text-[#5C665C] text-base leading-relaxed mb-8">
                Let&apos;s set up your garden profile in just a few steps. This helps us give you personalized advice based on your location, climate, and goals.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3"><Check className="w-5 h-5 text-[#1A4331] mt-0.5" /><span className="text-[#2D241E]">Personalized crop recommendations</span></div>
                <div className="flex items-start gap-3"><Check className="w-5 h-5 text-[#1A4331] mt-0.5" /><span className="text-[#2D241E]">Weather-based watering advice</span></div>
                <div className="flex items-start gap-3"><Check className="w-5 h-5 text-[#1A4331] mt-0.5" /><span className="text-[#2D241E]">AI garden coach tailored to your skill level</span></div>
                <div className="flex items-start gap-3"><Check className="w-5 h-5 text-[#1A4331] mt-0.5" /><span className="text-[#2D241E]">Harvest predictions based on your climate</span></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboarding-step-2">
              <div className="w-16 h-16 bg-[#8AB4F8]/20 rounded-full flex items-center justify-center mb-6">
                <MapPin className="w-8 h-8 text-[#1A4331]" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A4331] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('onboarding.whereGarden')}</h2>
              <p className="text-[#5C665C] text-base leading-relaxed mb-8">We use this to provide accurate weather and seasonal advice. Your location stays private.</p>
              <div className="space-y-4">
                <button onClick={detectLocation} disabled={locationLoading} data-testid="detect-location-button" className="w-full bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-4 px-6 rounded-full transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  <MapPin className="w-5 h-5" />
                  {locationLoading ? 'Detecting...' : 'Detect My Location'}
                </button>
                <div className="text-center text-sm text-[#5C665C]">or enter manually</div>
                <input type="text" value={data.location} onChange={(e) => setData({ ...data, location: e.target.value })} placeholder="e.g., Auckland, New Zealand" data-testid="location-manual-input" className="w-full px-4 py-3 bg-[#F0F2EB] border-transparent focus:border-[#8EAA97] focus:ring-2 focus:ring-[#8EAA97]/20 rounded-xl outline-none transition-all" />
                <p className="text-xs text-[#7A6B57]">We use your location to find your exact New Zealand climate zone automatically — no need to pick a region.</p>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboarding-step-3">
              <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mb-6">
                <Award className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A4331] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('onboarding.experienceTitle')}</h2>
              <p className="text-[#5C665C] text-base leading-relaxed mb-8">This helps us tailor advice to your skill level.</p>
              <div className="space-y-3">
                {[
                  { value: 'beginner', label: 'Beginner', desc: 'Just starting out, want simple guidance' },
                  { value: 'intermediate', label: 'Intermediate', desc: 'Growing for 1-3 years, ready for more depth' },
                  { value: 'advanced', label: 'Advanced', desc: 'Experienced grower, want detailed agronomy' }
                ].map((option) => (
                  <button key={option.value} onClick={() => setData({ ...data, skill_level: option.value })} data-testid={`skill-${option.value}-button`} className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${data.skill_level === option.value ? 'border-[#1A4331] bg-[#1A4331]/5' : 'border-[#F0F2EB] hover:border-[#8EAA97]'}`}>
                    <div className="font-semibold text-[#2D241E]">{option.label}</div>
                    <div className="text-sm text-[#5C665C] mt-1">{option.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div data-testid="onboarding-step-4">
              <div className="w-16 h-16 bg-[#8EAA97]/20 rounded-full flex items-center justify-center mb-6">
                <Sprout className="w-8 h-8 text-[#1A4331]" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A4331] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('onboarding.gardenDetails')}</h2>
              <p className="text-[#5C665C] text-base leading-relaxed mb-8">A quick overview helps us plan better recommendations.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#2D241E] mb-2">Garden Type</label>
                  <select value={data.garden_type} onChange={(e) => setData({ ...data, garden_type: e.target.value })} data-testid="garden-type-select" className="w-full px-4 py-3 bg-[#F0F2EB] border-transparent focus:border-[#8EAA97] focus:ring-2 focus:ring-[#8EAA97]/20 rounded-xl outline-none transition-all">
                    <option value="">Select garden type</option>
                    <option value="backyard">Backyard / In-ground beds</option>
                    <option value="raised_beds">Raised garden beds</option>
                    <option value="containers">Containers / Pots</option>
                    <option value="greenhouse">Greenhouse</option>
                    <option value="balcony">Balcony / Patio</option>
                    <option value="mixed">Mixed setup</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#2D241E] mb-2">Garden Size</label>
                  <select value={data.garden_size} onChange={(e) => setData({ ...data, garden_size: e.target.value })} data-testid="garden-size-select" className="w-full px-4 py-3 bg-[#F0F2EB] border-transparent focus:border-[#8EAA97] focus:ring-2 focus:ring-[#8EAA97]/20 rounded-xl outline-none transition-all">
                    <option value="">Select size</option>
                    <option value="tiny">Tiny (containers only)</option>
                    <option value="small">Small (under 10m²)</option>
                    <option value="medium">Medium (10-50m²)</option>
                    <option value="large">Large (50-200m²)</option>
                    <option value="huge">Huge (200m²+)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#2A3528] mb-2">How do you prefer to grow?</label>
                  <p className="text-xs text-[#7A6B57] mb-3">Choose the style that best matches your garden. You can change this later.</p>
                  <div className="space-y-2">
                    {[
                      { value: 'conventional', label: 'Conventional', desc: 'Practical mainstream methods, including synthetic fertilisers or treatments where appropriate.' },
                      { value: 'organic', label: 'Organic', desc: 'Compost, organic fertilisers, biological controls, and non-synthetic pest/disease options.' },
                      { value: 'regenerative', label: 'Regenerative', desc: 'Soil biology, compost, cover crops, biodiversity, minimal disturbance, mulch, long-term ecosystem health.' },
                      { value: 'mixed', label: 'Mixed / Flexible', desc: 'Best practical option for the situation, keeping safety, sustainability, and results in mind.' }
                    ].map(opt => (
                      <button key={opt.value} onClick={() => setData({ ...data, growing_preference: opt.value })} data-testid={`preference-${opt.value}-button`} className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${data.growing_preference === opt.value ? 'border-[#1A4331] bg-[#1A4331]/5' : 'border-[#E5EBDD] hover:border-[#8EAA97] bg-[#FAF6EC]'}`}>
                        <div className="font-semibold text-[#1A4331]">{opt.label}</div>
                        <div className="text-xs text-[#7A6B57] mt-1 leading-relaxed">{opt.desc}</div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-[#7A6B57] mt-3 italic">Not sure? Choose Mixed / Flexible. Homegrown will keep advice practical and explain the options.</p>
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div data-testid="onboarding-step-5">
              <div className="w-16 h-16 bg-[#C9A85B]/20 rounded-full flex items-center justify-center mb-6">
                <Award className="w-8 h-8 text-[#C9A85B]" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A4331] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('onboarding.valueCommunity')}</h2>
              <p className="text-[#7A6B57] text-base leading-relaxed mb-6">Tell us how much you want to share with other gardeners.</p>

              <div>
                <label className="block text-sm font-semibold text-[#2A3528] mb-2">Community preference</label>
                <p className="text-xs text-[#7A6B57] mb-3">Would you like to see what other gardeners near you are growing?</p>
                <div className="space-y-2">
                  {[
                    { value: 'off', label: 'Keep my garden private', desc: 'No community feed, no sharing, no leaderboards.' },
                    { value: 'view_only', label: 'View local inspiration only', desc: 'See what others grow but never share anything yourself.' },
                    { value: 'anonymous', label: 'Share anonymously', desc: 'Contribute harvests without showing your name.' },
                    { value: 'region', label: 'Share with region only', desc: 'Show your first name and broad region (no exact address).' },
                    { value: 'public', label: 'Public gardener profile', desc: 'Full profile, visible in leaderboards and digests.' }
                  ].map(opt => (
                    <button key={opt.value} onClick={() => setData({ ...data, community_mode: opt.value })} data-testid={`community-mode-${opt.value}-button`} className={`w-full text-left p-3 rounded-2xl border-2 transition-all ${data.community_mode === opt.value ? 'border-[#1A4331] bg-[#1A4331]/5' : 'border-[#E5EBDD] hover:border-[#8EAA97] bg-[#FAF6EC]'}`}>
                      <div className="font-semibold text-[#1A4331] text-sm">{opt.label}</div>
                      <div className="text-xs text-[#7A6B57] mt-0.5">{opt.desc}</div>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#7A6B57] mt-3 italic">You can change this anytime in Settings.</p>
              </div>
            </div>
          )}

          {step === 6 && (
            <div data-testid="onboarding-step-6">
              <div className="w-16 h-16 bg-[#C66B4A]/20 rounded-full flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-[#C66B4A]" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A4331] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>What do you love to grow?</h2>
              <p className="text-[#7A6B57] text-base leading-relaxed mb-8">Pick your favorites — we&apos;ll prioritise advice for these crops.</p>
              <div className="grid grid-cols-3 gap-2">
                {popularCrops.map((crop) => (
                  <button key={crop} onClick={() => toggleFavorite(crop)} data-testid={`favorite-${crop.toLowerCase()}-button`} className={`py-3 px-4 rounded-full text-sm font-medium transition-all ${data.favorites.includes(crop) ? 'bg-[#1A4331] text-white' : 'bg-[#FAF6EC] text-[#2A3528] hover:bg-[#8EAA97]/20'}`}>
                    {crop}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#7A6B57] mt-4 text-center">You can add more later from the Crop Library</p>
            </div>
          )}

          {step === 7 && (
            <div data-testid="onboarding-step-7">
              <div className="w-16 h-16 bg-[#8AB4F8]/20 rounded-full flex items-center justify-center mb-6">
                <Sparkles className="w-8 h-8 text-[#1A4331]" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A4331] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Would you like an intelligent garden coach?</h2>
              <p className="text-[#5C665C] text-base leading-relaxed mb-8">Homegrown can include an AI coach that answers your questions, reads your live weather, and tailors advice to your garden. It&apos;s entirely your call — and you can change it later.</p>
              <div className="space-y-3">
                {aiOptions.map((opt) => (
                  <button key={opt.value} onClick={() => setData({ ...data, ai_choice: opt.value })} data-testid={`ai-choice-${opt.value}-button`} className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${data.ai_choice === opt.value ? 'border-[#1A4331] bg-[#1A4331]/5' : 'border-[#F0F2EB] hover:border-[#8EAA97] bg-[#FAF6EC]'}`}>
                    <div className="font-semibold text-[#1A4331]">{opt.label}</div>
                    <div className="text-sm text-[#7A6B57] mt-1 leading-relaxed">{opt.desc}</div>
                  </button>
                ))}
              </div>
              {data.ai_choice === 'a' && (
                <p className="text-sm text-[#C66B4A] mt-4 leading-relaxed" data-testid="ai-choice-a-note">
                  You&apos;ve chosen to keep Homegrown AI-free. All AI features will be permanently hidden — you&apos;ll need to contact support to turn them back on.
                </p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#1A4331]/5">
            {step > 1 ? (
              <button onClick={() => setStep(step - 1)} data-testid="back-button" className="text-[#5C665C] hover:text-[#1A4331] font-medium transition-colors">{t('common.back')}</button>
            ) : <div />}
            <button onClick={handleNext} disabled={loading} data-testid="next-button" className="bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-3 px-8 rounded-full transition-colors disabled:opacity-50 flex items-center gap-2">
              {loading ? `${t('common.loading')}` : step === totalSteps ? t('onboarding.getStarted') : t('common.continue')}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
